import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, X, Expand } from 'lucide-react';
import { optimized, srcSetFor } from '../../utils/img';

// Room photo gallery: large image, thumbnail strip and a full-screen lightbox.
export default function RoomGallery({ images = [], alt = '', label = 'Photos' }) {
  const [i, setI] = useState(0);
  const [zoom, setZoom] = useState(false);
  if (!images.length) return null;
  const go = (d) => setI((c) => (c + d + images.length) % images.length);
  return (
    <section aria-label={label} data-testid="room-gallery">
      <div className="relative aspect-[16/10] rounded-2xl overflow-hidden bg-slate-100 group">
        <img src={optimized(images[i], 1200)} srcSet={srcSetFor(images[i], [800, 1200, 1600])} sizes="(min-width: 1024px) 800px, 100vw" alt={`${alt} — ${i + 1}`} className="w-full h-full object-cover" data-testid="room-gallery-main" />
        {images.length > 1 && (
          <>
            <button type="button" onClick={() => go(-1)} aria-label="Previous photo" className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 hover:bg-white text-slate-800 flex items-center justify-center shadow" data-testid="room-gallery-prev"><ChevronLeft className="w-5 h-5" /></button>
            <button type="button" onClick={() => go(1)} aria-label="Next photo" className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 hover:bg-white text-slate-800 flex items-center justify-center shadow" data-testid="room-gallery-next"><ChevronRight className="w-5 h-5" /></button>
          </>
        )}
        <button type="button" onClick={() => setZoom(true)} className="absolute bottom-3 right-3 bg-black/60 hover:bg-black/75 text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5" data-testid="room-gallery-zoom"><Expand className="w-3.5 h-3.5" />{i + 1} / {images.length}</button>
      </div>
      {images.length > 1 && (
        <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
          {images.map((img, idx) => (
            <button type="button" key={img} onClick={() => setI(idx)} className={`flex-shrink-0 w-24 h-16 rounded-lg overflow-hidden border-2 transition-all ${idx === i ? 'border-amber-500' : 'border-transparent opacity-60 hover:opacity-100'}`} aria-label={`Photo ${idx + 1}`}>
              <img src={optimized(img, 400)} alt={`${alt} — ${idx + 1}`} loading="lazy" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
      {zoom && (
        <div className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center p-4" onClick={() => setZoom(false)} data-testid="room-lightbox">
          <img src={optimized(images[i], 1600)} alt={alt} className="max-h-full max-w-full object-contain" />
          <button type="button" onClick={() => setZoom(false)} aria-label="Close" className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"><X className="w-5 h-5" /></button>
          {images.length > 1 && (
            <>
              <button type="button" onClick={(e) => { e.stopPropagation(); go(-1); }} aria-label="Previous" className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"><ChevronLeft className="w-6 h-6" /></button>
              <button type="button" onClick={(e) => { e.stopPropagation(); go(1); }} aria-label="Next" className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"><ChevronRight className="w-6 h-6" /></button>
            </>
          )}
        </div>
      )}
    </section>
  );
}
